import { useEffect, useRef, useState } from 'react';
import { useRouter } from 'next/router';

import { useDMPermissionPending } from '@/components/pages/conversations/SocialInbox/hooks/useDMPermissionPending';
import useNetworkOauthUrls from '@/utils/hooks/signup/useNetworkOauthUrls';
import useUserFBPermissions from '@/utils/hooks/swr/useUserFBPermissions';
import useTranslation from '@/utils/hooks/translation/useTranslation';
import useAcl from '@/utils/hooks/user/useAcl';
import useActiveSocialProfile from '@/utils/hooks/user/useActiveSocialProfile';
import useUserRole from '@/utils/hooks/user/useUserRole';

import { aclFeatures } from '@/lib/acl/aclFeatures';
import { PERMISSIONS } from '@/lib/permissions/socialPermissions';
import { FACEBOOK, INSTAGRAM } from '@/lib/socialProfile';

import Loader from '@/components/base/Loader';
import QuickActionsModal, { ModalAction } from '@/components/composite/Modals/QuickActionsModal';

let activeUnlockDMPermissionPopinInstance: symbol | null = null;

export const UnlockDMPermissionPopin = ({
    onSyncPermissions
}: {
    onSyncPermissions: (isSyncingPermissions: boolean) => void;
}) => {
    // Temporary solution to avoid multiple double renders of the popin between desktop and mobile
    // TODO: Remove this once we have a proper solution to handle multiple double renders of desktop and mobile
    const instanceIdRef = useRef<symbol>(Symbol('unlock-dm-permission-popin-instance'));
    if (activeUnlockDMPermissionPopinInstance === null) {
        activeUnlockDMPermissionPopinInstance = instanceIdRef.current;
    }
    const isActivePopinInstance = activeUnlockDMPermissionPopinInstance === instanceIdRef.current;

    const { t } = useTranslation('conversations');
    const router = useRouter();
    const { user: userFBPermissions, mutate } = useUserFBPermissions();
    const { getOauthUrl } = useNetworkOauthUrls();
    const { isAdmin, isManager } = useUserRole();
    const { hasAcl } = useAcl();
    const { activeSocialProfile } = useActiveSocialProfile();
    const [isPendingDismissed, setIsPendingDismissed] = useState<boolean>(false);

    const canCreateDirectMessages = hasAcl(aclFeatures.direct_messages.base.create);
    const canReadDirectMessages = hasAcl(aclFeatures.direct_messages.base.read);
    const hasAccessToDirectMessages = canCreateDirectMessages || canReadDirectMessages;

    const isInstagram = activeSocialProfile?.social_network_type === INSTAGRAM;
    const requiredPermission = isInstagram ? PERMISSIONS.INSTAGRAM_DM : PERMISSIONS.MESSENGER_DM;
    const hasPermission = userFBPermissions?.permissions.includes(requiredPermission);
    const { shouldPollPermissions, getOauthPendingRedirectQuery } = useDMPermissionPending({
        hasPermission,
        mutate
    });

    /* Propagates the syncing permissions state to the parent component
    to display a loading state while the permissions are syncing */
    useEffect(() => {
        onSyncPermissions(shouldPollPermissions);
    }, [shouldPollPermissions, onSyncPermissions]);

    useEffect(
        () => () => {
            if (activeUnlockDMPermissionPopinInstance === instanceIdRef.current) {
                activeUnlockDMPermissionPopinInstance = null;
            }
        },
        []
    );

    if (!isActivePopinInstance) {
        return null;
    }

    const unlockPermission = () => {
        window.location.assign(
            getOauthUrl({
                network: FACEBOOK,
                permissions: hasAccessToDirectMessages ? [requiredPermission] : [],
                redirect: {
                    query: getOauthPendingRedirectQuery()
                }
            })
        );
    };

    const backToComments = () => {
        router.push('/conversations/comments');
    };

    const canUnlockPermission = isAdmin || isManager;

    const actions: ModalAction[] = [
        {
            label: t('direct_messages.unlock_dm_permission_popin.grant_permission'),
            onClick: unlockPermission,
            icon: 'link',
            hidden: !canUnlockPermission,
            testId: 'grant-permission-button'
        },
        {
            label: t('direct_messages.unlock_dm_permission_popin.go_back'),
            className: !canUnlockPermission && 'w-[100%]',
            onClick: backToComments,
            testId: 'go-back-button'
        }
    ];

    if (hasPermission || isPendingDismissed) {
        return null;
    }

    if (shouldPollPermissions) {
        return (
            <QuickActionsModal
                title={t('direct_messages.unlock_dm_permission_popin.pending_title')}
                actions={[]}
                withCloseButton={true}
                onDismiss={() => setIsPendingDismissed(true)}
                size="lg"
                containerClassName="p-10"
                titleClassName="text-left"
                descriptionClassName="text-md text-left font-GMedium text-icoGray-500"
                footerClassName="flex w-full gap-3 p-0"
                testId="unlock-dm-permission-popin"
                isOpen>
                <div className="text-md flex justify-center text-left font-GMedium text-icoGray-500">
                    <Loader className="mr-2 h-6 w-6 text-icoBlue" />
                    {t('direct_messages.unlock_dm_permission_popin.pending_description')}
                </div>
            </QuickActionsModal>
        );
    }

    const descriptionNoRights = t(
        isInstagram
            ? 'direct_messages.unlock_dm_permission_popin.description_no_rights_instagram'
            : 'direct_messages.unlock_dm_permission_popin.description_no_rights_messenger'
    );

    let title;
    let description;
    if (isInstagram) {
        title = t('direct_messages.unlock_dm_permission_popin.title_instagram');
        description = t('direct_messages.unlock_dm_permission_popin.description_instagram');
    } else {
        title = t('direct_messages.unlock_dm_permission_popin.title_messenger');
        description = t('direct_messages.unlock_dm_permission_popin.description_messenger');
    }

    return (
        <QuickActionsModal
            title={title}
            description={canUnlockPermission ? description : descriptionNoRights}
            actions={actions}
            withCloseButton={false}
            size="lg"
            containerClassName="p-10"
            titleClassName="text-left"
            descriptionClassName="text-md text-left font-GMedium text-icoGray-500"
            footerClassName="flex w-full gap-3 p-0"
            testId="unlock-dm-permission-popin"
            isOpen
        />
    );
};
